import { normalizeAlgebra } from "@/lib/tool-kit/algebra/parse";
import {
  exponentsOf,
  type Poly,
  polySub,
  polyVariables,
} from "@/lib/tool-kit/algebra/poly";
import {
  countOf,
  parseSides,
  type StatementError,
} from "@/lib/tool-kit/algebra/statement";
import {
  absBig,
  BIG_ONE,
  BIG_ZERO,
  gcdBig,
  lcmBig,
} from "@/lib/tool-kit/math/bigint";
import {
  addR,
  divR,
  formatFraction,
  formatPolynomial,
  formatStyled,
  fromBigInt,
  intR,
  isZeroR,
  mulR,
  type NumberStyle,
  negR,
  RATIONAL_ZERO,
  type Rational,
  signR,
  subR,
} from "@/lib/tool-kit/math/rational";

/**
 * Systems of linear equations — 2 or 3 unknowns, up to four equations.
 *
 * The working is the elimination method as it is taught: combine two equations so one
 * unknown drops out, using whole-number multipliers (never fractions until the last
 * "divide" step), repeat until each unknown has an equation to itself. Every step keeps
 * the whole system on show. The same rows give Cramer's rule when there is one solution
 * and as many equations as unknowns.
 */
export const MAX_UNKNOWNS = 3;
export const MAX_EQUATIONS = 4;

export type Row = Rational[];

export type SystemError =
  | StatementError
  | { code: "too-few"; token: "" }
  | { code: "too-many-equations"; token: string }
  | { code: "too-many-unknowns"; token: string }
  | { code: "nonlinear"; token: string }
  | { code: "no-unknowns"; token: "" };

export interface ReadSystem {
  variables: string[];
  /** One row per equation: a coefficient per variable, then the constant on the right. */
  rows: Row[];
}

export type SystemReading =
  | { ok: true; system: ReadSystem }
  | { ok: false; error: SystemError };

const LINE_BREAK = /[\n;]+/;

interface LinearForm {
  coefficients: Map<string, Rational>;
  constant: Rational;
}

function linearForm(poly: Poly): LinearForm | null {
  const coefficients = new Map<string, Rational>();
  let constant = RATIONAL_ZERO;
  for (const [key, value] of Object.entries(poly)) {
    if (key === "") {
      constant = value;
      continue;
    }
    const exponents = exponentsOf(key);
    const names = Object.keys(exponents);
    if (names.length !== 1 || exponents[names[0]] !== 1) {
      return null;
    }
    coefficients.set(names[0], value);
  }
  return { coefficients, constant };
}

/** Read the text box: one equation per line (or separated by semicolons). */
export function readSystem(input: string): SystemReading {
  const lines = normalizeAlgebra(input.replace(/\n/g, ";"))
    .split(LINE_BREAK)
    .map((line) => line.trim())
    .filter((line) => line !== "");
  if (lines.length < 2) {
    return { ok: false, error: { code: "too-few", token: "" } };
  }
  if (lines.length > MAX_EQUATIONS) {
    return {
      ok: false,
      error: { code: "too-many-equations", token: String(MAX_EQUATIONS) },
    };
  }
  const forms: LinearForm[] = [];
  const names = new Set<string>();
  for (const [index, line] of lines.entries()) {
    const equals = countOf(line, "=");
    if (equals !== 1) {
      return {
        ok: false,
        error: {
          code: equals === 0 ? "no-relation" : "many-relations",
          token: String(index + 1),
        },
      };
    }
    const sides = parseSides(line.split("="));
    if (!sides.ok) {
      return { ok: false, error: sides.error };
    }
    const net = polySub(sides.polys[0], sides.polys[1]);
    const form = linearForm(net);
    if (form === null) {
      return {
        ok: false,
        error: { code: "nonlinear", token: String(index + 1) },
      };
    }
    for (const name of polyVariables(net)) {
      names.add(name);
    }
    forms.push(form);
  }
  const variables = [...names].sort();
  if (variables.length === 0) {
    return { ok: false, error: { code: "no-unknowns", token: "" } };
  }
  if (variables.length > MAX_UNKNOWNS) {
    return {
      ok: false,
      error: { code: "too-many-unknowns", token: String(MAX_UNKNOWNS) },
    };
  }
  const rows = forms.map((form) => [
    ...variables.map((name) => form.coefficients.get(name) ?? RATIONAL_ZERO),
    negR(form.constant),
  ]);
  return { ok: true, system: { variables, rows } };
}

export type SystemStepKind = "scale" | "swap" | "combine" | "divide";

export interface SystemStep {
  kind: SystemStepKind;
  rows: Row[];
  /** 1-based equation numbers the step touches. */
  target: number;
  source?: number;
  /** For `combine`: new target = multiplier × target + other × source. */
  multiplier?: bigint;
  other?: bigint;
  /** The unknown being eliminated or isolated. */
  variable?: string;
  /** A common factor divided out of the new equation, when there was one. */
  reducedBy?: bigint;
  /** For `scale`: the factor; for `divide`: the divisor. */
  factor?: Rational;
  /** What the equation now says when every unknown has cancelled. */
  degenerate?: "false" | "true";
}

export interface ParametricValue {
  constant: Rational;
  /** Coefficient of each parameter, in the order of `free`. */
  params: Rational[];
}

export interface CramerDeterminant {
  label: string;
  expression: string;
  value: Rational;
}

export interface SystemSolution {
  steps: SystemStep[];
  outcome: "unique" | "none" | "infinite";
  /** For `unique`: the value of each unknown. */
  values: Rational[];
  /** For `infinite`: each unknown in terms of the free ones. */
  general: ParametricValue[];
  /** Indexes of the unknowns left free. */
  free: number[];
  /** For `none`: the 1-based equation that became false. */
  contradiction: number | null;
  cramer: CramerDeterminant[] | null;
}

function cloneRows(rows: Row[]): Row[] {
  return rows.map((row) => [...row]);
}

function rowGcd(row: Row): bigint {
  let result = BIG_ZERO;
  for (const value of row) {
    result = gcdBig(result, absBig(value.num));
  }
  return result;
}

function scaleRowBy(row: Row, factor: Rational): Row {
  return row.map((value) => mulR(value, factor));
}

/** Which unknowns a row still mentions: all zero means the equation is degenerate. */
function degenerateOf(row: Row): "false" | "true" | undefined {
  const unknowns = row.slice(0, -1);
  if (unknowns.some((value) => !isZeroR(value))) {
    return undefined;
  }
  return isZeroR(row.at(-1) as Rational) ? "true" : "false";
}

interface State {
  rows: Row[];
  steps: SystemStep[];
}

function snapshot(state: State, step: Omit<SystemStep, "rows">): void {
  state.steps.push({ ...step, rows: cloneRows(state.rows) });
}

function clearDenominators(state: State): void {
  state.rows.forEach((row, index) => {
    let lcm = BIG_ONE;
    for (const value of row) {
      lcm = lcmBig(lcm, value.den);
    }
    if (lcm > BIG_ONE) {
      state.rows[index] = scaleRowBy(row, fromBigInt(lcm));
      snapshot(state, {
        kind: "scale",
        target: index + 1,
        factor: fromBigInt(lcm),
      });
    }
  });
}

/** The row to pivot on: the current one when its entry is not zero, else the first below that is. */
function choosePivot(rows: Row[], from: number, column: number): number {
  for (let r = from; r < rows.length; r += 1) {
    if (!isZeroR(rows[r][column])) {
      return r;
    }
  }
  return -1;
}

function combine(
  state: State,
  target: number,
  source: number,
  column: number,
  variable: string
): void {
  const f = state.rows[target][column].num;
  const p = state.rows[source][column].num;
  const g = gcdBig(absBig(p), absBig(f));
  const multiplier = absBig(p) / g;
  const other = (-f * (p < BIG_ZERO ? -BIG_ONE : BIG_ONE)) / g;
  let row = state.rows[target].map((value, index) =>
    addR(
      mulR(value, fromBigInt(multiplier)),
      mulR(state.rows[source][index], fromBigInt(other))
    )
  );
  const common = rowGcd(row);
  const reducedBy = common > BIG_ONE ? common : undefined;
  if (reducedBy) {
    row = scaleRowBy(row, divR(intR(1), fromBigInt(reducedBy)));
  }
  state.rows[target] = row;
  snapshot(state, {
    kind: "combine",
    target: target + 1,
    source: source + 1,
    multiplier,
    other,
    variable,
    reducedBy,
    degenerate: degenerateOf(row),
  });
}

function eliminate(
  state: State,
  variables: string[]
): { pivots: number[]; contradiction: number | null } {
  const pivots: number[] = [];
  let next = 0;
  for (let column = 0; column < variables.length; column += 1) {
    if (next >= state.rows.length) {
      break;
    }
    const pivot = choosePivot(state.rows, next, column);
    if (pivot === -1) {
      continue;
    }
    if (pivot !== next) {
      [state.rows[pivot], state.rows[next]] = [
        state.rows[next],
        state.rows[pivot],
      ];
      snapshot(state, { kind: "swap", target: next + 1, source: pivot + 1 });
    }
    for (let r = 0; r < state.rows.length; r += 1) {
      if (r !== next && !isZeroR(state.rows[r][column])) {
        combine(state, r, next, column, variables[column]);
        if (state.steps.at(-1)?.degenerate === "false") {
          return { pivots, contradiction: r + 1 };
        }
      }
    }
    pivots.push(column);
    next += 1;
  }
  return { pivots, contradiction: null };
}

function isolate(state: State, pivots: number[], variables: string[]): void {
  pivots.forEach((column, index) => {
    const pivot = state.rows[index][column];
    if (!(pivot.num === BIG_ONE && pivot.den === BIG_ONE)) {
      state.rows[index] = scaleRowBy(state.rows[index], divR(intR(1), pivot));
      snapshot(state, {
        kind: "divide",
        target: index + 1,
        variable: variables[column],
        factor: pivot,
      });
    }
  });
}

function parametric(
  rows: Row[],
  pivots: number[],
  free: number[],
  count: number
): ParametricValue[] {
  const result: ParametricValue[] = [];
  for (let column = 0; column < count; column += 1) {
    const freeIndex = free.indexOf(column);
    if (freeIndex !== -1) {
      result.push({
        constant: RATIONAL_ZERO,
        params: free.map((_, k) => (k === freeIndex ? intR(1) : RATIONAL_ZERO)),
      });
      continue;
    }
    const rowIndex = pivots.indexOf(column);
    const row = rows[rowIndex];
    result.push({
      constant: row.at(-1) as Rational,
      params: free.map((f) => negR(row[f])),
    });
  }
  return result;
}

function det2(m: Row[]): Rational {
  return subR(mulR(m[0][0], m[1][1]), mulR(m[0][1], m[1][0]));
}

function det3(m: Row[]): Rational {
  const minor = (c: number) => {
    const cols = [0, 1, 2].filter((k) => k !== c);
    return det2([
      [m[1][cols[0]], m[1][cols[1]]],
      [m[2][cols[0]], m[2][cols[1]]],
    ]);
  };
  return addR(
    subR(mulR(m[0][0], minor(0)), mulR(m[0][1], minor(1))),
    mulR(m[0][2], minor(2))
  );
}

export function determinant(m: Row[]): Rational {
  return m.length === 2 ? det2(m) : det3(m);
}

function bracket(value: Rational): string {
  const text = formatFraction(value);
  return text.startsWith("−") || text.includes("/") ? `(${text})` : text;
}

/** The determinant written out with its numbers: `2 × (−1) − 1 × 1`. */
function expansion(m: Row[]): string {
  const n = (r: number, c: number) => bracket(m[r][c]);
  if (m.length === 2) {
    return `${n(0, 0)} × ${n(1, 1)} − ${n(0, 1)} × ${n(1, 0)}`;
  }
  const minor = (c: number) => {
    const [p, q] = [0, 1, 2].filter((k) => k !== c);
    return `${n(1, p)} × ${n(2, q)} − ${n(1, q)} × ${n(2, p)}`;
  };
  return `${n(0, 0)} × (${minor(0)}) − ${n(0, 1)} × (${minor(1)}) + ${n(0, 2)} × (${minor(2)})`;
}

function cramerDeterminants(
  original: Row[],
  variables: string[]
): CramerDeterminant[] | null {
  const n = variables.length;
  if (original.length !== n || n < 2) {
    return null;
  }
  const coefficients = original.map((row) => row.slice(0, n));
  const make = (label: string, matrix: Row[]): CramerDeterminant => ({
    label,
    expression: expansion(matrix),
    value: determinant(matrix),
  });
  const result = [make("D", coefficients)];
  variables.forEach((name, column) => {
    const replaced = coefficients.map((row, r) =>
      row.map((value, c) => (c === column ? original[r][n] : value))
    );
    result.push(make(`D${name}`, replaced));
  });
  return result;
}

export function solveSystem(system: ReadSystem): SystemSolution {
  const { variables, rows } = system;
  const state: State = { rows: cloneRows(rows), steps: [] };
  clearDenominators(state);
  const { pivots, contradiction } = eliminate(state, variables);
  const base = {
    steps: state.steps,
    values: [] as Rational[],
    general: [] as ParametricValue[],
    free: [] as number[],
    contradiction,
    cramer: cramerDeterminants(rows, variables),
  };
  if (contradiction !== null) {
    return { ...base, outcome: "none" };
  }
  isolate(state, pivots, variables);
  const free = variables.map((_, i) => i).filter((i) => !pivots.includes(i));
  // A leftover equation with no unknowns is 0 = 0 (a false one stopped us above).
  if (free.length === 0) {
    const values = pivots.map((_, i) => state.rows[i].at(-1) as Rational);
    return { ...base, outcome: "unique", values };
  }
  return {
    ...base,
    outcome: "infinite",
    free,
    general: parametric(state.rows, pivots, free, variables.length),
  };
}

/** One equation of a system as text: `2x + 3y = 7`. */
export function formatRow(
  row: Row,
  variables: readonly string[],
  style: NumberStyle = "fraction",
  mark = "."
): string {
  const left = formatPolynomial(
    variables.map((name, i) => ({ coefficient: row[i], variable: name })),
    style,
    mark
  );
  const right = row.at(-1) as Rational;
  return `${left} = ${formatPolynomial([{ coefficient: right, variable: "" }], style, mark)}`;
}

export function signOf(value: Rational): number {
  return signR(value);
}

const NEEDS_BRACKETS = /^−|\//;

/**
 * An equation checked at a solution: `2 × (−1) + 3 × 3` and its value `7`. The total is
 * the left side evaluated, to be compared with the right side.
 */
export function checkRow(
  row: Row,
  values: readonly Rational[],
  style: NumberStyle = "fraction",
  mark = "."
): { working: string; total: string } {
  const parts: string[] = [];
  let sum = RATIONAL_ZERO;
  values.forEach((value, k) => {
    const c = row[k];
    if (isZeroR(c)) {
      return;
    }
    sum = addR(sum, mulR(c, value));
    const shown = formatStyled(value, style, mark);
    const wrapped = NEEDS_BRACKETS.test(shown) ? `(${shown})` : shown;
    const magnitude = formatStyled(signR(c) < 0 ? negR(c) : c, style, mark);
    const body = magnitude === "1" ? wrapped : `${magnitude} × ${wrapped}`;
    if (parts.length === 0) {
      parts.push(signR(c) < 0 ? `−${body}` : body);
    } else {
      parts.push(` ${signR(c) < 0 ? "−" : "+"} ${body}`);
    }
  });
  return {
    working: parts.length === 0 ? "0" : parts.join(""),
    total: formatStyled(sum, style, mark),
  };
}
